import { useEffect, useState } from "react";
import { FileDown, Trash2, MessageCircle, Mail, Phone, Receipt } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Link } from "react-router-dom";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "closed"];

export default function QuotationsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/rfq").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (rfq, status) => {
    try {
      await api.put(`/rfq/${rfq.id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (rfq) => {
    if (!window.confirm(`Delete enquiry from ${rfq.name}?`)) return;
    try {
      await api.delete(`/rfq/${rfq.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-quotations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Inbox</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">RFQ Inbox</h1>
        </div>
        <div className="text-sm text-slate-600 font-mono">{items.length} total</div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Reference</th>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Contact</th>
              <th className="text-left p-3 font-semibold">Product</th>
              <th className="text-left p-3 font-semibold">Status</th>
              <th className="text-left p-3 font-semibold">Date</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className={`border-t border-slate-200 ${r.status === "new" ? "bg-amber-50/40" : ""}`}>
                <td className="p-3 font-mono text-xs">RFQ-{r.id.slice(0, 8).toUpperCase()}</td>
                <td className="p-3">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.company || "—"}</div>
                </td>
                <td className="p-3 text-xs">
                  <a href={`tel:${r.mobile}`} className="flex items-center gap-1 hover:text-amber-600">
                    <Phone className="w-3 h-3" /> {r.mobile}
                  </a>
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-amber-600 mt-1">
                    <Mail className="w-3 h-3" /> {r.email}
                  </a>
                </td>
                <td className="p-3 text-xs">
                  <div className="font-semibold">{r.product_name || "—"}</div>
                  <div className="text-slate-500">Qty: {r.quantity || "—"}</div>
                </td>
                <td className="p-3">
                  <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                    <SelectTrigger className="h-8 w-32 rounded-none text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-xs font-mono text-slate-600">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 text-right space-x-1">
                  <Link
                    to={`/admin/estimated-quotations?from_rfq=${r.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 hover:bg-amber-600 text-slate-950 text-[10px] font-bold uppercase tracking-wider"
                    title="Convert to Quotation"
                    data-testid={`rfq-convert-${r.id}`}
                  >
                    <Receipt className="w-3 h-3" /> Quote
                  </Link>
                  {r.pdf_url && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${r.pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex p-2 hover:bg-slate-100"
                      title="Download PDF"
                      data-testid={`rfq-pdf-${r.id}`}
                    >
                      <FileDown className="w-4 h-4" />
                    </a>
                  )}
                  <a
                    href={`https://wa.me/91${r.mobile.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${r.name}, regarding your enquiry RFQ-${r.id.slice(0, 8).toUpperCase()}.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex p-2 hover:bg-green-50 text-[#25D366]"
                    title="Reply on WhatsApp"
                    data-testid={`rfq-wa-${r.id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </a>
                  <button onClick={() => del(r)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`rfq-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No enquiries yet.</div>}
      </div>
    </div>
  );
}
